import {isFeatureEnabled} from 'feature-flags';
import {FormField} from 'platform/components';
import {Box, Grid, Heading, Show, Space, VStack} from 'platform/foundation';

import {useCallback, useEffect} from 'react';
import {FieldPathValue} from 'react-hook-form';

import {isNilOrEmpty} from 'ramda-adjunct';

import {suffixTestId, TestIdProps, useBoolean} from 'shared/web';

import {useCatalogue} from '../../../hooks/useCatalogue';
import i18n from '../../../i18n/i18n';
import {featureFlags} from '../../../utils/featureFlags';
import {MAX_SEAT_COUNT} from '../constants/maxSeatCount';
import {WITHOUT_VAT} from '../constants/withoutVAT';
import {WITH_VAT} from '../constants/withVAT';
import {VehicleFormType} from '../types/VehicleFormType';
import {
  FILTER_FORM_FIELD_NAMES,
  getDependentFilterFieldNames,
} from '../utils/getDependentFilterFieldNames';
import {useCreatePageContext} from './CreatePageContext';
import {DrillDownPicker} from './DrillDownPicker';
import {MakeModelFields} from './MakeModelFields';
import {PowerFields} from './PowerFields';
import {SeparatorWithLabel} from './SeparatorWithLabel';
import {TrimLevelField} from './TrimLevelField';
import {VinParser} from './VinParser';

const seatCountOptions = Array.from(Array(MAX_SEAT_COUNT)).map((_, index) => ({
  value: `${index + 1}`,
  label: `${index + 1}`,
}));

export function VehicleFields(props: TestIdProps) {
  const [pendingVinValidation, startPendingVinValidation, finishPendingVinValidation] =
    useBoolean(false);
  const {
    formApi,
    control,
    values,
    yearOptions,
    fuelTypeOptions,
    transmissionOptions,
    bodyStyleOptions,
    driveOptions,
    powerOptions,
    has,
    isOptionsLoading,
  } = useCreatePageContext();
  const {doorOptions, colorOptions, interiorMaterialOptions} = useCatalogue();
  const {
    make,
    modelFamily,
    firstRegistrationOnYear,
    fuelType,
    transmission,
    drive,
    power,
    bodyStyle,
  } = values;

  const showFuelTypeSection = has.basicInfo;
  const showTransmissionSection = showFuelTypeSection && has.fuelType;
  const showDriveSection = showTransmissionSection && has.transmission;
  const showPowerSection = showDriveSection && has.drive;
  const showBodyStyleSection = showPowerSection && has.power;
  const showAdditionalSection = showBodyStyleSection && has.bodyStyle;

  const resetDependentFields = useCallback(
    (changedFieldName: keyof VehicleFormType) => {
      const dependentFields = getDependentFilterFieldNames(changedFieldName);
      dependentFields.forEach((fieldName) => {
        formApi.setValue(fieldName, undefined, {shouldTouch: false});
      });
      if (changedFieldName === 'make' || changedFieldName === 'modelFamily') {
        formApi.setValue('trimLevel', undefined, {shouldTouch: false});
      }
      control.onChange();
      formApi.trigger(changedFieldName);
    },
    [formApi, control]
  );

  const onFilterChange = useCallback(
    (
      name: keyof VehicleFormType,
      value: FieldPathValue<VehicleFormType, keyof VehicleFormType>
    ) => {
      formApi.setValue(name, value, {shouldTouch: true});
      control.onChange();
      formApi.trigger(name);
    },
    [formApi, control]
  );

  useEffect(() => {
    if (yearOptions.length === 1 && yearOptions[0].value !== firstRegistrationOnYear) {
      const onlyValue = yearOptions[0].value;
      onFilterChange('firstRegistrationOnYear', onlyValue);
      resetDependentFields('firstRegistrationOnYear');
    }
  }, [yearOptions, firstRegistrationOnYear, onFilterChange, resetDependentFields]);

  useEffect(() => {
    if (
      fuelTypeOptions.length === 1 &&
      fuelTypeOptions[0].value !== fuelType?.[0] &&
      showFuelTypeSection
    ) {
      const onlyValue = fuelTypeOptions[0].value;
      onFilterChange('fuelType', [onlyValue]);
      resetDependentFields('fuelType');
    }
  }, [fuelTypeOptions, fuelType, showFuelTypeSection, onFilterChange, resetDependentFields]);

  useEffect(() => {
    if (
      transmissionOptions.length === 1 &&
      transmissionOptions[0].value !== transmission?.[0] &&
      showTransmissionSection
    ) {
      const onlyValue = transmissionOptions[0].value;
      onFilterChange('transmission', [onlyValue]);
      resetDependentFields('transmission');
    }
  }, [
    transmissionOptions,
    transmission,
    showTransmissionSection,
    onFilterChange,
    resetDependentFields,
  ]);

  useEffect(() => {
    if (driveOptions.length === 1 && driveOptions[0].value !== drive?.[0] && showDriveSection) {
      const onlyValue = driveOptions[0].value;
      onFilterChange('drive', [onlyValue]);
      resetDependentFields('drive');
    }
  }, [driveOptions, drive, showDriveSection, onFilterChange, resetDependentFields]);

  useEffect(() => {
    if (
      bodyStyleOptions.length === 1 &&
      bodyStyleOptions[0].value !== bodyStyle?.[0] &&
      showBodyStyleSection
    ) {
      const onlyValue = bodyStyleOptions[0].value;
      onFilterChange('bodyStyle', [onlyValue]);
      resetDependentFields('bodyStyle');
    }
  }, [bodyStyleOptions, bodyStyle, showBodyStyleSection, onFilterChange, resetDependentFields]);

  useEffect(() => {
    if (
      powerOptions.length === 1 &&
      powerOptions[0].value !== power?.toString() &&
      showPowerSection
    ) {
      const onlyValue = Number(powerOptions[0].value);
      onFilterChange('power', onlyValue);
      resetDependentFields('power');
    }
  }, [powerOptions, power, showPowerSection, onFilterChange, resetDependentFields]);

  const validateFieldsWithDependencies = useCallback(() => {
    const fieldsToValidate = FILTER_FORM_FIELD_NAMES.filter((fieldName) => {
      const fieldState = formApi.getFieldState(fieldName);
      return !fieldState.error;
    });
    if (fieldsToValidate.length > 0) {
      formApi.trigger(fieldsToValidate);
    }
  }, [formApi]);

  // Revalidate after VIN decode + options load. Only fields without errors (VinParser validation has priority).
  useEffect(() => {
    if (pendingVinValidation && !isOptionsLoading) {
      validateFieldsWithDependencies();
      finishPendingVinValidation();
    }
  }, [
    pendingVinValidation,
    isOptionsLoading,
    validateFieldsWithDependencies,
    finishPendingVinValidation,
  ]);

  return (
    <VStack spacing={4}>
      <Heading size={2}>{i18n.t('page.create.labels.vehicleSpecification')}</Heading>
      <VinParser
        onDecodeSuccess={startPendingVinValidation}
        data-testid={suffixTestId('vinParser', props)}
      />
      <SeparatorWithLabel label={i18n.t('page.create.labels.basicInformation')} />
      <MakeModelFields
        onFilterFieldChange={resetDependentFields}
        data-testid={suffixTestId('makeModelFields', props)}
      />
      <Grid columns={[1, 1, 2]} spacing={6}>
        <FormField
          control={control}
          type="choice"
          name="firstRegistrationOnYear"
          label={i18n.t('page.create.labels.firstRegistrationOnYear')}
          isRequired
          isDisabled={isNilOrEmpty(make) || isNilOrEmpty(modelFamily)}
          options={yearOptions}
          onChange={() => resetDependentFields('firstRegistrationOnYear')}
          menuInPortal
          data-testid={suffixTestId('form-firstRegistrationOnYear', props)}
        />
        <TrimLevelField data-testid={suffixTestId('form-trimLevel', props)} />
      </Grid>
      <Show when={showFuelTypeSection}>
        <SeparatorWithLabel label={i18n.t('page.create.labels.detailedInformation')} />
        <FormField
          control={control}
          type="chips"
          name="fuelType"
          label={i18n.t('page.create.labels.fuelType')}
          isRequired
          isDeselectable={fuelTypeOptions.length > 1}
          options={fuelTypeOptions}
          onChange={() => resetDependentFields('fuelType')}
          data-testid={suffixTestId('form-fuelType', props)}
        />
      </Show>
      <Show when={showTransmissionSection}>
        <FormField
          control={control}
          type="chips"
          name="transmission"
          label={i18n.t('page.create.labels.transmission')}
          isRequired
          isDeselectable={transmissionOptions.length > 1}
          options={transmissionOptions}
          onChange={() => resetDependentFields('transmission')}
          data-testid={suffixTestId('form-transmission', props)}
        />
      </Show>
      <Show when={showDriveSection}>
        <FormField
          control={control}
          type="chips"
          name="drive"
          label={i18n.t('page.create.labels.drive')}
          isRequired
          isDeselectable={driveOptions.length > 1}
          options={driveOptions}
          onChange={() => resetDependentFields('drive')}
          data-testid={suffixTestId('form-drive', props)}
        />
      </Show>
      <Show when={showPowerSection}>
        <PowerFields
          power={power}
          fuelType={fuelType?.[0]}
          powerOptions={powerOptions}
          control={control}
          isManualEnterDisabled={isFeatureEnabled(featureFlags.PR_FORM_STATISTICS)}
          onFilterChange={onFilterChange}
          onPowerFieldChange={() => resetDependentFields('power')}
          data-testid={suffixTestId('form-power', props)}
        />
      </Show>
      <Show when={showBodyStyleSection}>
        <FormField
          control={control}
          type="chips"
          name="bodyStyle"
          label={i18n.t('page.create.labels.bodyStyle')}
          isRequired
          isDeselectable={bodyStyleOptions.length > 1}
          options={bodyStyleOptions}
          onChange={() => resetDependentFields('bodyStyle')}
          data-testid={suffixTestId('form-bodyStyle', props)}
        />
      </Show>
      <Show whenFeatureEnabled={featureFlags.PR_FORM_STATISTICS} when={showFuelTypeSection}>
        <DrillDownPicker data-testid={suffixTestId('createDrillDown', props)} />
      </Show>
      <Show when={showAdditionalSection}>
        <SeparatorWithLabel label={i18n.t('page.create.labels.additionalInformation')} />
        <Grid columns={[1, 1, 2]} spacing={6}>
          <Box flex={1}>
            <FormField
              control={control}
              type="number"
              name="mileage"
              label={`${i18n.t('page.create.labels.mileage')} (${i18n.t('general.metric.km')})`}
              isRequired
              data-testid={suffixTestId('form-mileage', props)}
            />
          </Box>
          <Show onDesktop onNotebook>
            <Space fillAvailable />
          </Show>
        </Grid>
        <FormField
          control={control}
          type="chips"
          name="doorCount"
          label={i18n.t('page.create.labels.doorCount')}
          options={doorOptions ?? []}
          isDeselectable
          data-testid={suffixTestId('form-doorCount', props)}
        />
        <FormField
          control={control}
          type="chips"
          name="seatCount"
          label={i18n.t('page.create.labels.seatCount')}
          options={seatCountOptions}
          isDeselectable
          data-testid={suffixTestId('form-seatCount', props)}
        />
        <FormField
          control={control}
          type="chips"
          name="isVatDeductible"
          label={i18n.t('page.create.labels.vatDeductible')}
          options={[
            {label: i18n.t('page.create.labels.yes'), value: WITH_VAT},
            {label: i18n.t('page.create.labels.no'), value: WITHOUT_VAT},
          ]}
          isDeselectable
          data-testid={suffixTestId('form-isVatDeductible', props)}
        />
        <FormField
          control={control}
          type="chips"
          name="exteriorColor"
          label={i18n.t('page.create.labels.exteriorColor')}
          options={colorOptions ?? []}
          isDeselectable
          data-testid={suffixTestId('form-exteriorColor', props)}
        />
        <FormField
          control={control}
          type="chips"
          name="interiorMaterial"
          label={i18n.t('page.create.labels.interiorMaterial')}
          options={interiorMaterialOptions ?? []}
          isDeselectable
          data-testid={suffixTestId('form-interiorMaterial', props)}
        />
      </Show>
    </VStack>
  );
}
